import axios from "axios";
import Bowser from "bowser";
import { get } from "lodash";
import { EVENTS } from "@upsolve/shared";
import { TTrackingEvent } from "@upsolve/shared/dist/types";
import { v4 as uuidV4 } from "uuid";
export const DeviceIdLocalStorageKey = "upsolveTrackingDeviceId";

export enum CTATrackingTarget {
  SCREENER = "CTA.myUpsolveNavigation",
  AJM = "CTA.AJM",
  LOGIN = "CTA.LOGIN",
  STUDENT_LOANS = "CTA.STUDENT_LOANS",
  SELECT_DEBT_RELIEF = "CTA.SELECT_DEBT_RELIEF"
}
// 'private' track call so we can conditionally chose to wrap in setTimeout below
const _track = async (
  event: string,
  fields: TTrackingEvent["fields"],
) => {
  try {
    const browserProps = Bowser.parse(window.navigator.userAgent);
    
    // Using amplitude's device id errored 90% of times in practice. It is unclear exactly why, but the possibilities are: NextJS's window not being defined caused an error with loading the amplitude script; cached pages prevented the id from working, ad blockers or tracking blockers prevented calls from going out, or something else.
    // using localStorage mitigates all of these issues, and using a random uuid is exactly what Amplitude does in most cases, except on iOs for example. Since we are sending a direct HTTP request to amplitude instead of using their client or server library, amplitude will treat the id we send them as a session id no matter what it comes from.
    let deviceId = localStorage.getItem(DeviceIdLocalStorageKey);
    if (!deviceId) {
      deviceId = uuidV4();
      localStorage.setItem(DeviceIdLocalStorageKey, deviceId);
    }

    // Build props
    const trackBody: TTrackingEvent = {
      type: event,
      fields: {
        deviceId: deviceId,
        browser: {
          name: browserProps?.browser.name ?? "",
          version: browserProps?.browser.version ?? "",
        },
        canonicalUrl: fields.canonicalUrl ?? get(document.querySelector('link[rel="canonical"]'), "href"),
        componentName: fields.componentName,
        componentVersion: fields.componentVersion,
        actionId: fields.actionId,
        actionMethod: fields.actionMethod,
        os: {
          name: browserProps?.os?.name ?? "",
          versionName: browserProps?.os?.versionName ?? "",
          version: browserProps?.os?.version ?? "",
        },
        pageContext: {
          contentCreationMethod: fields.pageContext?.contentCreationMethod,
          contentGroup: fields.pageContext?.contentGroup,
          localeSpecific: fields.pageContext?.localeSpecific,
          jsonldTypes: typeof document !== "undefined" ? Array.from(document.querySelectorAll("script[type='application/ld+json']")).map((dom) => JSON.parse(dom.innerHTML)["@type"]) : undefined,
          pageComponentName: fields.pageContext?.pageComponentName,
          pageComponentVersion: fields.pageContext?.pageComponentVersion,
          publishedAt: fields.pageContext?.publishedAt,
          searchTermTargets: Array.from(fields.pageContext?.searchTermTargets ?? []).filter((stt) => stt),
          slug: fields.pageContext?.slug,
          tags: fields.pageContext?.tags,
          title: fields.pageContext?.title ?? (typeof document !== "undefined" ? document.title : undefined),
          updatedAt: fields.pageContext?.updatedAt,
          wordCount: fields.pageContext?.wordCount,
        },
        path: window.location.pathname,
        platform: {
          type: browserProps?.platform?.type ?? "",
          vendor: browserProps?.platform?.vendor ?? "",
        },
        referrer: document.referrer,
        screenHeight: window.innerHeight,
        screenWidth: window.innerWidth,
        url: window.location.href,
      },
    };

    // TRACK
    await axios.post(`${UPSOLVE_API_URL}/v1/tracking`, trackBody);
  } catch (error) {
    console.warn(error);
  }
}

// HACK: Doing a setTimeout to let react components render, specifically react helmet which may modify the page title/jsonld
export const track = async (
  event: string,
  fields: TTrackingEvent["fields"],
) => event === EVENTS.PAGE_VIEW ? setTimeout(() => _track(event, fields)) : _track(event, fields);
